/**
 * Programa trabajo no crítico fuera de la ventana de interacción temprana.
 *
 * Por qué existe: las medidas de campo (Google Search Console, 2026-07) daban
 * INP p75 = 414 ms en la home y 303 ms en el grupo de landings. El perfilado con
 * `long-animation-frame` sobre producción atribuía los frames bloqueantes a los
 * scripts de terceros (`posthog-recorder` 152 ms, `gtag/js` 133 ms, `gtm.js`
 * 106 ms), y todos arrancaban en el evento `load` — justo cuando el visitante da
 * su primer toque. El resultado eran ~90 ms de *input delay* heredado.
 *
 * `scheduleWhenIdle` espera a que la carga termine y luego cede el hilo hasta
 * que el navegador está realmente ocioso, con un `timeout` como red de
 * seguridad para que el trabajo se ejecute igualmente en páginas que nunca
 * llegan a estar idle.
 */

type IdleTask = () => void

interface ScheduleOptions {
  /** Máxima espera antes de ejecutar de todas formas. */
  timeout?: number
}

const DEFAULT_TIMEOUT_MS = 3000

/**
 * Ejecuta `task` una sola vez, cuando el hilo principal esté ocioso tras `load`.
 * Devuelve una función para cancelar la ejecución pendiente.
 */
export function scheduleWhenIdle(task: IdleTask, options: ScheduleOptions = {}): () => void {
  if (typeof window === 'undefined') return () => {}

  const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS
  let cancelled = false
  let hasRun = false
  let timerId: number | undefined
  let idleId: number | undefined

  const run = () => {
    if (cancelled || hasRun) return
    hasRun = true
    // Un tercero que explote no debe impedir que se programen los demás.
    try {
      task()
    } catch (error) {
      console.warn('[scheduleWhenIdle] deferred task failed:', error)
    }
  }

  const schedule = () => {
    if (cancelled) return

    const idle = window.requestIdleCallback
    if (typeof idle === 'function') {
      idleId = idle(run, { timeout })
      return
    }

    // Safari < 18.2 y WebViews antiguos no traen requestIdleCallback.
    // Se usa el `setTimeout` global, no `window.setTimeout`: siempre existe,
    // y así programar nunca puede lanzar y hacer fallar al llamante.
    timerId = setTimeout(run, timeout) as unknown as number
  }

  const onLoad = () => schedule()

  // El guard de `addEventListener` es el mismo patrón que ya usaba
  // loadGtagScript: sin él, un `window` sin la API haría lanzar a `schedule`.
  if (document.readyState !== 'complete' && typeof window.addEventListener === 'function') {
    window.addEventListener('load', onLoad, { once: true })
  } else {
    schedule()
  }

  return () => {
    cancelled = true
    if (typeof window.removeEventListener === 'function') {
      window.removeEventListener('load', onLoad)
    }
    if (timerId !== undefined) clearTimeout(timerId)
    if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId)
    }
  }
}
